import { useQuery } from '@tanstack/react-query'
import { Table, Empty, Tag, Spin } from '@arco-design/web-react'
import type { ColumnProps } from '@arco-design/web-react/es/Table'
import { useTranslation } from 'react-i18next'
import { getSelfLogsApi, type LogListResponse } from '@/api/logs'
import { renderModelTag, renderTimestamp, renderUseTime } from '@/utils/render'
import { renderQuotaCompact, formatNumberCompact } from '@/utils/quota'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { LogEntry } from '@/types'

function RecentRecordCard({ record }: { record: LogEntry }) {
  const { t } = useTranslation()
  const modelName = record.modelName || '-'
  const isStream = record.isStream

  return (
    <div className="rounded-xl border border-[var(--console-border)] p-3 space-y-2 bg-[var(--console-surface)]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {modelName === '-' ? (
            <span className="text-[var(--console-text-subtle)] text-sm">-</span>
          ) : (
            renderModelTag(modelName)
          )}
        </div>
        <span className="text-xs text-[var(--console-text-subtle)] shrink-0">
          {renderTimestamp(record.createdAt ?? 0)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--console-text-muted)]">
        {record.tokenName && (
          <span className="truncate max-w-[140px]" title={record.tokenName}>
            {record.tokenName}
          </span>
        )}
        <span>
          {t('tt.recentRecords.cost')} {renderQuotaCompact(record.quota ?? 0)}
        </span>
        <span>
          {t('tt.recentRecords.useTime')} {renderUseTime(record.useTime ?? 0)}
        </span>
        {isStream ? (
          <Tag size="small" color="arcoblue">
            {t('tt.recentRecords.stream')}
          </Tag>
        ) : (
          <Tag size="small" color="gray">
            {t('tt.recentRecords.nonStream')}
          </Tag>
        )}
      </div>
    </div>
  )
}

/**
 * Dashboard 最近使用记录：GET /api/log/self 最新 10 条。
 */
export function RecentRecordsPanel() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'recent-logs'],
    queryFn: () => getSelfLogsApi({ page: 1, page_size: 10 }),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  const items: LogEntry[] = (data as LogListResponse | undefined)?.items ?? []

  const desktopColumns: ColumnProps<LogEntry>[] = [
    {
      title: t('时间'),
      dataIndex: 'createdAt',
      width: 170,
      render: (_, record) => renderTimestamp(record.createdAt ?? 0),
    },
    {
      title: t('模型'),
      dataIndex: 'modelName',
      render: (_, record) => {
        const name = record.modelName || '-'
        if (name === '-') return <span className="text-[var(--console-text-subtle)]">-</span>
        return renderModelTag(name)
      },
    },
    {
      title: t('tt.recentRecords.tokenName'),
      dataIndex: 'tokenName',
      render: (_, record) => {
        const name = record.tokenName
        if (!name) return <span className="text-[var(--console-text-subtle)]">-</span>
        return <span className="text-xs text-[var(--console-text-body)]">{name}</span>
      },
    },
    {
      title: t('tt.recentRecords.useTime'),
      dataIndex: 'useTime',
      width: 90,
      render: (_, record) => renderUseTime(record.useTime ?? 0),
    },
    {
      title: t('输入 Token'),
      dataIndex: 'promptTokens',
      width: 96,
      align: 'right',
      render: (_, record) => (
        <span className="tabular-nums text-xs text-[var(--console-text-body)]">
          {formatNumberCompact(record.promptTokens ?? 0)}
        </span>
      ),
    },
    {
      title: t('输出 Token'),
      dataIndex: 'completionTokens',
      width: 96,
      align: 'right',
      render: (_, record) => (
        <span className="tabular-nums text-xs text-[var(--console-text-body)]">
          {formatNumberCompact(record.completionTokens ?? 0)}
        </span>
      ),
    },
    {
      title: t('tt.recentRecords.cost'),
      dataIndex: 'quota',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <span className="tabular-nums text-xs font-medium text-[var(--console-text-h)]">
          {renderQuotaCompact(record.quota ?? 0)}
        </span>
      ),
    },
    {
      title: t('返回方式'),
      dataIndex: 'isStream',
      width: 96,
      render: (_, record) => {
        const isStream = record.isStream
        return isStream ? (
          <Tag size="small" color="arcoblue">
            {t('tt.recentRecords.stream')}
          </Tag>
        ) : (
          <Tag size="small" color="gray">
            {t('tt.recentRecords.nonStream')}
          </Tag>
        )
      },
    },
  ]

  const mobileColumns: ColumnProps<LogEntry>[] = [
    {
      title: t('时间'),
      dataIndex: 'createdAt',
      width: 150,
      render: (_, record) => renderTimestamp(record.createdAt ?? 0),
    },
    {
      title: t('模型'),
      dataIndex: 'modelName',
      render: (_, record) => {
        const name = record.modelName || '-'
        if (name === '-') return <span className="text-[var(--console-text-subtle)]">-</span>
        return renderModelTag(name)
      },
    },
    {
      title: t('tt.recentRecords.tokenName'),
      dataIndex: 'tokenName',
      render: (_, record) => {
        const name = record.tokenName
        if (!name) return <span className="text-[var(--console-text-subtle)]">-</span>
        return <span className="text-xs text-[var(--console-text-body)] truncate max-w-[100px]" title={name}>{name}</span>
      },
    },
    {
      title: t('tt.recentRecords.cost'),
      dataIndex: 'quota',
      width: 88,
      align: 'right',
      render: (_, record) => (
        <span className="tabular-nums text-xs font-medium text-[var(--console-text-h)]">
          {renderQuotaCompact(record.quota ?? 0)}
        </span>
      ),
    },
    {
      title: t('返回方式'),
      dataIndex: 'isStream',
      width: 88,
      render: (_, record) => {
        const isStream = record.isStream
        return isStream ? (
          <Tag size="small" color="arcoblue">
            {t('tt.recentRecords.stream')}
          </Tag>
        ) : (
          <Tag size="small" color="gray">
            {t('tt.recentRecords.nonStream')}
          </Tag>
        )
      },
    },
  ]

  const columns = isMobile ? mobileColumns : desktopColumns

  return (
    <section className="tt-card overflow-hidden">
      <header className="px-4 md:px-6 py-4 border-b border-[var(--console-divider)]">
        <h3 className="text-sm font-semibold text-[var(--console-text-h)]">{t('tt.recentRecords.title')}</h3>
      </header>

      {isMobile ? (
        <div className="p-3 space-y-2">
          {isLoading ? (
            <div className="py-10 flex justify-center">
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10">
              <Empty description={t('tt.recentRecords.empty')} />
            </div>
          ) : (
            items.map((record) => <RecentRecordCard key={record.id} record={record} />)
          )}
        </div>
      ) : (
        <div className="px-2 py-1">
          <Table<LogEntry>
            rowKey="id"
            columns={columns}
            data={items}
            loading={isLoading}
            pagination={false}
            size="small"
            stripe={false}
            border={false}
            noDataElement={
              <div className="py-10">
                <Empty description={t('tt.recentRecords.empty')} />
              </div>
            }
          />
        </div>
      )}
    </section>
  )
}
